import { ReactNode, useState } from 'react';
import { useRecoilValue } from 'recoil';
import history from 'service/customHistory';
import { useConditionalEffect, usePrevious } from 'react-delta';

import { queryTimeRangeState } from 'components/common/Managers/RouteStateManager.state';
import Dialog from 'components/common/Dialog/Dialog';
import Link from 'components/common/Link/Link';
import { elliptifyLabelText } from 'common/util/graph';

import { createEventsPageUrl, DashboardPlotData } from 'components/dashboards/dashboardData';
import MarkerIcon from '@mui/icons-material/FiberManualRecord';
import { GraphConfigType } from 'components/dashboards/types';
import { prefilterState } from 'components/dashboards/dashboards.state';

import './PlotZoom.scss';

/**
 * Modal dialog that prompts for which plot to use when navigating to the graph page.
 *
 * @prop showModal          This modal is shown when true
 * @prop graphCconfig       The config stanza for this graph
 * @prop plots              The list of plots to show
 * @prop close              Callback for when the modal is closed
 *
 */

type PlotZoomProps = {
  showModal: boolean;
  graphConfig: GraphConfigType;
  plots: DashboardPlotData[];
  close: () => void;
};

function PlotZoom({
  showModal,
  close,
  graphConfig,
  plots,
}: PlotZoomProps): JSX.Element {

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const prefilter = useRecoilValue(prefilterState);

  return (
    <Dialog
      title="Which Plot?"
      show={showModal}
      close={close}
      noAutoFocus
      footer={{ omit: true }}
    >
      <div className="PlotZoom__Desc">
        This graph has multiple plots, each based on different log events.
        <br />
        Select a plot to view in search.
      </div>
      {plots.map((plot, plotIndex): ReactNode => (
        <div className="PlotZoom__Entry" key={Math.random()} role="navigation">
          <MarkerIcon className="PlotZoom__Marker" htmlColor={plot.color} />
          <Link href={createEventsPageUrl(startTime, endTime, graphConfig, plots, plotIndex, true, false, prefilter)}>{elliptifyLabelText(plot.label || 'Untitled', 100, true)}</Link>
        </div>
      ))}
    </Dialog>
  );
}

/**
 * Component that either navigates directly to the graph page or shows the "which plot" dialog,
 * depending on whether or not the dialog is multiplot.
 *
 * @prop graphCconfig       The config stanza for this graph
 * @prop plots              The list of plots to show
 * @prop doNavigate         The redirect happens or the dialog is shown
 * @prop onClose            Callback for when the modal is closed
 *
 */

type NavPlotToSearchProps = {
  graphConfig: GraphConfigType;
  plots: DashboardPlotData[] | undefined;
  doNavigate: boolean;
  onClose: () => void;
  href?: string;
};

export const NavPlotToSearch = ({
  graphConfig,
  plots,
  onClose,
  doNavigate,
  href,
}: NavPlotToSearchProps): JSX.Element => {
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const [showPlotPicker, setShowPlotPicker] = useState(false);
  const prefilter = useRecoilValue(prefilterState);

  const handleGraphClick = (plotIndex: number): void => {
    setShowPlotPicker(false);
    if (href) {
      history.push(href);
    } else {
      history.push(createEventsPageUrl(startTime, endTime, graphConfig, plots, plotIndex, true, false, prefilter));
    }
  };

  const prevValue = usePrevious(doNavigate);
  useConditionalEffect(() => {
    if (doNavigate && plots) {
      if (plots.length === 1 || graphConfig.breakdownFacet || graphConfig.query) {
        handleGraphClick(0);
        onClose();
      } else {
        setShowPlotPicker(true);
      }
    }
  }, doNavigate !== prevValue);

  if (plots && plots.length > 1) {
    return <>{showPlotPicker && <PlotZoom graphConfig={graphConfig} showModal={showPlotPicker} plots={plots} close={(): void => { setShowPlotPicker(false); onClose(); }} />}</>;
  }
  return <></>;
};

export default PlotZoom;
